import type { DimensionScore } from '../../utils/api-client/api-schemas.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { BAND_LABELS, type ScoreBand, formatScorePercent, toScoreBand } from './scorecard-bands.ts'
import * as styles from './scorecard-total-donut.css.ts'

/**
 * The mean of the dimension scores, as one number.
 *
 * A plain mean, because the dimensions carry equal weight by design — the same principle the risk
 * reference drawer states in prose. Dimensions the analysis could not score are **excluded rather than
 * counted as zero**: a rubric that could not be applied is not evidence of a weak posture, and averaging
 * it in as zero would let an unanswerable question drag the headline number down.
 *
 * Returns `undefined` when nothing is scorable, which the donut renders as an unknown ring rather than
 * as `0%` — those two things mean opposite things.
 */
export const meanScore = (scores: number[]): number | undefined => {
  const scorable = scores.filter(score => Number.isFinite(score))
  if (scorable.length === 0) {
    return undefined
  }
  return scorable.reduce((total, score) => total + score, 0) / scorable.length
}

const RADIUS = 42
const STROKE = 12

export type ScorecardTotalDonutProps = {
  /** Mean of the dimension scores, 0–1. `undefined` when nothing could be scored. */
  score: number | undefined
  /** How many dimensions fed the mean, for the accessible description. */
  scoredCount: number
  totalCount: number
}

/**
 * The cumulative score as a ring.
 *
 * `pathLength={100}` makes the dash array *be* the percentage, so no circumference arithmetic is needed
 * and the arc cannot drift out of sync with the number printed inside it.
 */
export const ScorecardTotalDonut: React.FC<ScorecardTotalDonutProps> = ({ score, scoredCount, totalCount }) => {
  const band: ScoreBand = score === undefined ? 'unknown' : toScoreBand(score)
  const percent = score === undefined ? 0 : Math.round(score * 100)
  const label =
    score === undefined
      ? `No cumulative score: none of the ${totalCount} dimensions could be scored`
      : `Cumulative score ${percent} percent, averaged across ${scoredCount} of ${totalCount} dimensions`

  return (
    <div className={styles.donut}>
      <svg className={styles.ring} viewBox="0 0 100 100" role="img" aria-label={label}>
        <circle className={styles.track} cx="50" cy="50" r={RADIUS} strokeWidth={STROKE} fill="none" />
        {score !== undefined && (
          <circle
            className={styles.arc({ band })}
            cx="50"
            cy="50"
            r={RADIUS}
            strokeWidth={STROKE}
            fill="none"
            strokeLinecap="round"
            pathLength={100}
            strokeDasharray={`${percent} ${100 - percent}`}
            // Start at twelve o'clock; SVG's zero is at three.
            transform="rotate(-90 50 50)"
          />
        )}
      </svg>
      <p className={styles.value({ band })} aria-hidden>
        {score === undefined ? '—' : formatScorePercent(score).replace('%', '')}
      </p>
    </div>
  )
}

/**
 * A plain-language reading of the cumulative score.
 *
 * **Everything here is derived from the scores themselves** — the band, the counts, and the weakest
 * dimension by name. Nothing is asserted that the numbers below do not already say, because this sits
 * above the very cards a reader will check it against, and a summary that overreaches is worse than none.
 *
 * The weak dimensions get named rather than merely counted: "three score weak" sends you hunting, and the
 * whole point of a summary is to say where to look.
 */
export const riskScoreSummary = (dimensions: DimensionScore[]): string => {
  const scorable = dimensions.filter(dimension => Number.isFinite(dimension.score))
  const unscoredCount = dimensions.length - scorable.length

  if (scorable.length === 0) {
    return `None of the ${dimensions.length} ${pluralize(dimensions.length, 'dimension', 'dimensions')} below could be scored, so there is no cumulative risk score for this repository.`
  }

  const mean = meanScore(scorable.map(dimension => dimension.score)) ?? 0
  const band = toScoreBand(mean)
  const ranked = [...scorable].sort((a, b) => a.score - b.score)
  const dragging = ranked.filter(dimension => {
    const dimensionBand = toScoreBand(dimension.score)
    return dimensionBand === 'weak' || dimensionBand === 'critical'
  })

  // "the N dimensions below average X%" reads as "below average". Say "average" once, as the verb.
  const headline = `${BAND_LABELS[band]} overall — ${scorable.length} scored ${pluralize(scorable.length, 'dimension averages', 'dimensions average')} ${formatScorePercent(mean)}.`

  const named = dragging
    .slice(0, 3)
    .map(dimension => `${dimension.displayName} (${formatScorePercent(dimension.score)})`)
    .join(', ')
  const unnamedCount = dragging.length - Math.min(dragging.length, 3)
  const detail =
    dragging.length === 0
      ? 'No dimension falls below adequate, so no single area is holding the posture back.'
      : `${dragging.length} of ${scorable.length} ${pluralize(dragging.length, 'is', 'are')} weak or critical: ${named}${unnamedCount > 0 ? `, and ${unnamedCount} more` : ''}.`

  const caveat =
    unscoredCount === 0
      ? undefined
      : `${unscoredCount} ${pluralize(unscoredCount, 'dimension', 'dimensions')} could not be scored and ${pluralize(unscoredCount, 'is', 'are')} excluded from the average.`

  return [headline, detail, caveat].filter(Boolean).join(' ')
}
